import { AxiosResponse } from 'axios';
import {
  all,
  AllEffect,
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import {
  requestApiSaga,
  retryRequestApiSaga,
} from 'b2b-apps/shop/common/redux/api/sagas';
import { reportError } from 'b2b-apps/shop/common/redux/errors/actionCreators';
import { parseError } from 'b2b-apps/shop/common/utils/api/error';
import { Address } from 'b2b-apps/shop/myAccount/addresses/api/Address';
import {
  MY_ACCOUNT_MESSAGE,
  showMyAccountToast,
} from 'b2b-apps/shop/myAccount/common/utils/messages';
import {
  AddAddressAction,
  AddressType,
  ClearAccountAddressAction,
  EditAddressAction,
  FetchAddressAction,
  RemoveAddressAction,
} from 'b2b-common/core/account/api/Address.types';
import {
  clearAccountAddress,
  fetchAddressFulfilled,
  fetchAddressRejected,
} from './actionCreators';
import {
  ADD_ADDRESS,
  EDIT_ADDRESS,
  FETCH_ADDRESS,
  REMOVE_ADDRESS,
} from './actionTypes';

function* fetchAddressSaga(
  action: FetchAddressAction,
): Generator<CallEffect | PutEffect, any, AxiosResponse<AddressType>> {
  const { code } = action.payload;

  try {
    const response = yield call(retryRequestApiSaga, Address.fetch(code));
    yield put(
      fetchAddressFulfilled(code, response.data),
    );
  } catch (error) {
    yield put(fetchAddressRejected(code, parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* addAddressSaga(action: AddAddressAction): Generator<CallEffect | PutEffect, any, AxiosResponse> {
  const { addressData, callback } = action.payload;

  try {
    yield call(requestApiSaga, Address.addAddress(addressData));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successCreateAddress);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorCreateAddress);
    callback(false);
    yield put(reportError(action.type, error));
  }
}

function* editAddressSaga(action: EditAddressAction): Generator<CallEffect | PutEffect, any, AxiosResponse> {
  const { code, addressData, callback } = action.payload;

  try {
    yield call(requestApiSaga, Address.editAddress(code, addressData));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successEditAddress);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorEditAddress);
    callback(false);
    yield put(
      reportError(action.type, error, {
        code,
      }),
    );
  }
}

function* removeAddressSaga(
  action: RemoveAddressAction,
): Generator<CallEffect | PutEffect, any, AxiosResponse> {
  const { code, callback } = action.payload;

  try {
    yield call(requestApiSaga, Address.removeAddress(code));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successRemoveAddress);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorRemoveAddress);
    callback(false);
    yield put(
      reportError(action.type, error, {
        code,
      }),
    );
  }
}

function* clearAddressSaga(): Generator<PutEffect, any, ClearAccountAddressAction> {
  yield put(clearAccountAddress());
}

function* addressSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(FETCH_ADDRESS, fetchAddressSaga),
    takeLatest(ADD_ADDRESS, addAddressSaga),
    takeLatest(EDIT_ADDRESS, editAddressSaga),
    takeLatest(REMOVE_ADDRESS, removeAddressSaga),
    takeLatest([EDIT_ADDRESS, REMOVE_ADDRESS], clearAddressSaga),
  ]);
}

export {
  addressSaga,
};
